import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Box,
  Text,
  Icon,
  InputPrimitive,
  // @ts-expect-error - this will be resolved at build time
} from "@xsolla/xui-primitives";
import { useResolvedTheme, type ThemeOverrideProps } from "@xsolla/xui-core";
import { Spinner } from "@xsolla/xui-spinner";

export interface AutocompleteOption {
  id: string;
  label: string;
  description?: string;
  icon?: React.ReactNode;
  disabled?: boolean;
}

export interface AutocompleteProps extends ThemeOverrideProps {
  value?: string;
  placeholder?: string;
  onValueChange?: (value: string) => void;
  onSelect?: (option: string | AutocompleteOption) => void;
  /** Simple string options for basic usage */
  options?: string[];
  /** Rich options with id, label, description, icon */
  list?: AutocompleteOption[];
  isLoading?: boolean;
  size?: "xl" | "lg" | "md" | "sm" | "xs";
  state?: "default" | "hover" | "focus" | "disable" | "error";
  label?: string;
  errorLabel?: string;
  iconLeft?: React.ReactNode;
  chevronRight?: boolean;
  filled?: boolean;
  /** Maximum height of the dropdown (default: 250) */
  maxHeight?: number;
  /** Width of the dropdown (default: matches input width) */
  dropdownWidth?: number | string;
  /** Empty state message when no options match */
  emptyMessage?: string;
  /** Test ID for testing frameworks */
  testID?: string;
}

const SearchIcon = () => (
  <svg
    width="100%"
    height="100%"
    viewBox="0 0 18 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M8.25 14.25C11.5637 14.25 14.25 11.5637 14.25 8.25C14.25 4.93629 11.5637 2.25 8.25 2.25C4.93629 2.25 2.25 4.93629 2.25 8.25C2.25 11.5637 4.93629 14.25 8.25 14.25Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M15.75 15.75L12.4875 12.4875"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ChevronDown = () => (
  <svg
    width="100%"
    height="100%"
    viewBox="0 0 18 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M4.5 6.75L9 11.25L13.5 6.75"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ChevronUp = () => (
  <svg
    width="100%"
    height="100%"
    viewBox="0 0 18 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M13.5 11.25L9 6.75L4.5 11.25"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CloseIcon = () => (
  <svg
    width="100%"
    height="100%"
    viewBox="0 0 18 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M13.5 4.5L4.5 13.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M4.5 4.5L13.5 13.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Map Autocomplete sizes to ContextMenu sizes
const mapToContextMenuSize = (
  size: AutocompleteProps["size"]
): "sm" | "md" | "lg" | "xl" => {
  switch (size) {
    case "xs":
    case "sm":
      return "sm";
    case "md":
      return "md";
    case "lg":
      return "lg";
    case "xl":
      return "xl";
    default:
      return "md";
  }
};

export const Autocomplete: React.FC<AutocompleteProps> = ({
  value: propValue,
  placeholder = "Search...",
  onValueChange,
  onSelect,
  options = [],
  list,
  isLoading = false,
  size = "md",
  state: externalState,
  label,
  errorLabel,
  iconLeft = <SearchIcon />,
  chevronRight = true,
  filled = true,
  maxHeight = 250,
  dropdownWidth,
  emptyMessage = "No results found",
  testID,
  themeMode,
  themeProductContext,
}) => {
  const { theme } = useResolvedTheme({ themeMode, themeProductContext });
  const [internalValue, setInternalValue] = useState(propValue || "");
  const [isFocused, setIsFocused] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef<any>(null);
  const inputRef = useRef<any>(null);

  const value = propValue !== undefined ? propValue : internalValue;
  const state = externalState || (isFocused ? "focus" : "default");

  const isDisable = state === "disable";
  const isError = state === "error";
  const isFocus = state === "focus";

  const sizeStyles = theme.sizing.input(size);
  const inputColors = theme.colors.control.input;
  const contextMenuSize = mapToContextMenuSize(size);
  const menuSizeStyles = theme.sizing.contextMenu(contextMenuSize);

  // Determine which options to use (list takes priority over options)
  const normalizedOptions: AutocompleteOption[] =
    list ||
    options.map((opt, index) => ({
      id: String(index),
      label: opt,
    }));

  const hasOptions = normalizedOptions.length > 0;
  // Show dropdown when focused and either: has options, is loading, or has typed something (to show empty message)
  const showDropdown = isFocus && (hasOptions || isLoading || value.length > 0);

  // Reset active index when dropdown closes
  useEffect(() => {
    if (!showDropdown) {
      setActiveIndex(-1);
    }
  }, [showDropdown]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsFocused(false);
      }
    };

    if (isFocused) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isFocused]);

  // Escape key handling
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isFocused) {
        setIsFocused(false);
      }
    };

    if (isFocused) {
      document.addEventListener("keydown", handleEscape);
    }
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isFocused]);

  const handleInputChange = (text: string) => {
    if (!isDisable) {
      setInternalValue(text);
      if (onValueChange) onValueChange(text);
      // Don't call setIsFocused here - onFocus handler already handles it
      // and calling it here causes extra re-renders that can interfere with typing
      setActiveIndex(-1);
    }
  };

  const handleSelect = useCallback(
    (option: AutocompleteOption) => {
      setInternalValue(option.label);
      setIsFocused(false);

      // Call onSelect with appropriate type
      if (onSelect) {
        // If using list prop, return the full option object
        // If using options prop (strings), return just the label string
        if (list) {
          onSelect(option);
        } else {
          onSelect(option.label);
        }
      }
      if (onValueChange) onValueChange(option.label);
    },
    [list, onSelect, onValueChange]
  );

  const handleClear = (e: any) => {
    e.stopPropagation();
    handleInputChange("");
  };

  // Helper to find next enabled option index
  const findNextEnabledIndex = (
    currentIndex: number,
    direction: 1 | -1
  ): number => {
    const length = normalizedOptions.length;
    let nextIndex = currentIndex;
    for (let i = 0; i < length; i++) {
      nextIndex = (nextIndex + direction + length) % length;
      if (!normalizedOptions[nextIndex].disabled) {
        return nextIndex;
      }
    }
    return -1; // All options are disabled
  };

  // Keyboard navigation in input
  const handleInputKeyDown = (event: React.KeyboardEvent) => {
    if (!showDropdown || normalizedOptions.length === 0) return;

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setActiveIndex((prev) => findNextEnabledIndex(prev, 1));
        break;
      case "ArrowUp":
        event.preventDefault();
        setActiveIndex((prev) => findNextEnabledIndex(prev, -1));
        break;
      case "Enter":
        event.preventDefault();
        if (activeIndex >= 0 && activeIndex < normalizedOptions.length) {
          const option = normalizedOptions[activeIndex];
          if (!option.disabled) {
            handleSelect(option);
          }
        }
        break;
      case "Tab":
        setIsFocused(false);
        break;
    }
  };

  // Resolve colors
  let backgroundColor = inputColors.bg;
  let borderColor = inputColors.border;

  if (isDisable) {
    backgroundColor = inputColors.bgDisable;
    borderColor = inputColors.borderDisable;
  } else if (isError) {
    borderColor = theme.colors.border.alert;
  } else if (isFocus) {
    backgroundColor = theme.colors.control.focus.bg;
    borderColor = theme.colors.border.brand;
  } else if (state === "hover") {
    backgroundColor = inputColors.bgHover;
    borderColor = inputColors.borderHover;
  }

  // Handle filled override if provided
  if (filled === false && !isFocus && !isError && state !== "hover") {
    backgroundColor = "transparent";
  }

  const textColor = isDisable ? inputColors.textDisable : inputColors.text;
  const placeholderColor = inputColors.placeholder;
  const iconColor = isDisable ? inputColors.textDisable : inputColors.text;

  // Get colors for dropdown items
  const getItemBackgroundColor = (index: number, disabled?: boolean) => {
    if (activeIndex === index && !disabled) {
      return theme.colors.control.input.bgHover;
    }
    return "transparent";
  };

  return (
    <Box
      testID={testID}
      ref={containerRef}
      flexDirection="column"
      gap={4}
      width="100%"
      position="relative"
    >
      {label && (
        <Text
          color={theme.colors.content.secondary}
          fontSize={sizeStyles.fontSize - 2}
          fontWeight="500"
          marginBottom={4}
        >
          {label}
        </Text>
      )}
      <Box
        backgroundColor={backgroundColor}
        borderColor={borderColor}
        borderWidth={borderColor !== "transparent" ? 1 : 0}
        borderRadius={sizeStyles.radius}
        height={sizeStyles.height}
        paddingHorizontal={sizeStyles.paddingHorizontal}
        flexDirection="row"
        alignItems="center"
        gap={10}
        position="relative"
        hoverStyle={
          !isDisable && !isFocus && !isError
            ? {
                backgroundColor: inputColors.bgHover,
                borderColor: inputColors.borderHover,
              }
            : undefined
        }
      >
        {iconLeft && (
          <Box alignItems="center" justifyContent="center">
            <Icon size={sizeStyles.iconSize} color={iconColor}>
              {iconLeft}
            </Icon>
          </Box>
        )}

        <Box flex={1} height="100%" justifyContent="center">
          <InputPrimitive
            ref={inputRef}
            value={value}
            placeholder={placeholder}
            onChangeText={handleInputChange}
            onFocus={() => !isDisable && setIsFocused(true)}
            onKeyDown={handleInputKeyDown}
            disabled={isDisable}
            color={textColor}
            fontSize={sizeStyles.fontSize}
            placeholderTextColor={placeholderColor}
            role="combobox"
            aria-expanded={showDropdown}
            aria-haspopup="listbox"
            aria-controls="autocomplete-listbox"
            aria-activedescendant={
              activeIndex >= 0
                ? `autocomplete-option-${normalizedOptions[activeIndex]?.id}`
                : undefined
            }
          />
        </Box>

        <Box flexDirection="row" alignItems="center" gap={4}>
          {value.length > 0 && !isDisable && (
            <Box onPress={handleClear} padding={2}>
              <Icon size={sizeStyles.iconSize - 2} color={iconColor}>
                <CloseIcon />
              </Icon>
            </Box>
          )}

          {chevronRight && (
            <Box
              alignItems="center"
              justifyContent="center"
              onPress={(e: any) => {
                e.stopPropagation();
                if (!isDisable) {
                  const newFocusState = !isFocused;
                  setIsFocused(newFocusState);
                  // Focus input when opening dropdown
                  if (newFocusState && inputRef.current) {
                    inputRef.current.focus();
                  }
                }
              }}
              style={{ cursor: isDisable ? "not-allowed" : "pointer" }}
            >
              <Icon size={sizeStyles.iconSize} color={iconColor}>
                {isFocus ? <ChevronUp /> : <ChevronDown />}
              </Icon>
            </Box>
          )}
        </Box>
      </Box>

      {/* Dropdown Menu */}
      {showDropdown && (
        <Box
          position="absolute"
          top={sizeStyles.height + (label ? 28 : 0) + 4}
          left={0}
          right={0}
          backgroundColor={theme.colors.background.secondary}
          borderColor={theme.colors.border.secondary}
          borderWidth={1}
          borderRadius={theme.shape.contextMenu[contextMenuSize].borderRadius}
          paddingVertical={menuSizeStyles.paddingVertical}
          width={dropdownWidth}
          style={{
            zIndex: 1000,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            maxHeight,
            overflowY: "auto",
          }}
          id="autocomplete-listbox"
          role="listbox"
          aria-label="Autocomplete suggestions"
        >
          {isLoading ? (
            <Box
              padding={16}
              alignItems="center"
              justifyContent="center"
              minHeight={60}
            >
              <Spinner
                size="md"
                color={theme.colors.control.brand.primary.bg}
              />
            </Box>
          ) : hasOptions ? (
            normalizedOptions.map((option, index) => (
              <Box
                key={option.id}
                id={`autocomplete-option-${option.id}`}
                role="option"
                aria-selected={activeIndex === index}
                aria-disabled={option.disabled}
                flexDirection="row"
                alignItems={option.description ? "flex-start" : "center"}
                gap={menuSizeStyles.gap}
                paddingHorizontal={menuSizeStyles.itemPaddingHorizontal}
                paddingVertical={menuSizeStyles.itemPaddingVertical}
                backgroundColor={getItemBackgroundColor(index, option.disabled)}
                hoverStyle={
                  !option.disabled
                    ? { backgroundColor: theme.colors.control.input.bgHover }
                    : undefined
                }
                pressStyle={
                  !option.disabled
                    ? { backgroundColor: theme.colors.control.input.bgDisable }
                    : undefined
                }
                onPress={() => !option.disabled && handleSelect(option)}
                style={{
                  cursor: option.disabled ? "not-allowed" : "pointer",
                  opacity: option.disabled ? 0.5 : 1,
                }}
              >
                {/* Leading Icon */}
                {option.icon && (
                  <Box
                    width={menuSizeStyles.iconSize}
                    marginTop={option.description ? 2 : 0}
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Icon
                      size={menuSizeStyles.iconSize}
                      color={
                        option.disabled
                          ? theme.colors.content.tertiary
                          : theme.colors.content.secondary
                      }
                    >
                      {option.icon}
                    </Icon>
                  </Box>
                )}

                {/* Content: Label and Description */}
                <Box flex={1} flexDirection="column" gap={2}>
                  <Text
                    color={
                      option.disabled
                        ? theme.colors.control.input.textDisable
                        : theme.colors.content.primary
                    }
                    fontSize={menuSizeStyles.fontSize}
                    fontWeight="400"
                    lineHeight={menuSizeStyles.fontSize + 2}
                  >
                    {option.label}
                  </Text>
                  {option.description && (
                    <Text
                      color={theme.colors.content.tertiary}
                      fontSize={menuSizeStyles.descriptionFontSize}
                      lineHeight={menuSizeStyles.descriptionFontSize + 2}
                    >
                      {option.description}
                    </Text>
                  )}
                </Box>
              </Box>
            ))
          ) : (
            <Box padding={16} alignItems="center">
              <Text
                color={theme.colors.content.tertiary}
                fontSize={menuSizeStyles.fontSize}
              >
                {emptyMessage}
              </Text>
            </Box>
          )}
        </Box>
      )}

      {isError && errorLabel && (
        <Text
          color={theme.colors.content.alert.primary}
          fontSize={sizeStyles.fontSize - 2}
          marginTop={4}
        >
          {errorLabel}
        </Text>
      )}
    </Box>
  );
};
